import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { PerformanceComparison, TimeFormatKeys } from './campaign-statistics-types';

interface Props {
  performanceComparison: PerformanceComparison[];

  campaignIdToName(id: string): string;
}

const PerformanceComparisonTable: React.FC<Props> = ({ performanceComparison = [], campaignIdToName }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  function formatConversionTime(seconds: number) {
    const secondsInHour = 3600;
    const secondsInMinute = 60;
    const secondsInDay = secondsInHour * 24;
    const secondsInWeek = secondsInDay * 7;

    const weeks = Math.floor(seconds / secondsInWeek);
    const days = Math.floor((seconds % secondsInWeek) / secondsInDay);
    const hours = Math.floor((seconds % secondsInDay) / secondsInHour);
    const minutes = Math.floor((seconds % secondsInHour) / secondsInMinute);

    const parts = [];
    if (weeks > 0) parts.push(t(weeks > 1 ? TimeFormatKeys.WEEKS : TimeFormatKeys.WEEK, { count: weeks }));
    if (days > 0) parts.push(t(days > 1 ? TimeFormatKeys.DAYS : TimeFormatKeys.DAY, { count: days }));
    if (hours > 0) parts.push(t(hours > 1 ? TimeFormatKeys.HOURS : TimeFormatKeys.HOUR, { count: hours }));
    if (minutes > 0) parts.push(t(minutes > 1 ? TimeFormatKeys.MINUTES : TimeFormatKeys.MINUTE, { count: minutes }));

    return parts.join(', ') || t(TimeFormatKeys.NO_TIME);
  }

  const hasConnectionRequests = performanceComparison.some((item) => item.hasConnectionRequests);

  return (
    <div className={classes.performanceComparisonTable}>
      <div className={classes.header}>
        <Typography className={classes.titleText}>{t('campaignComparison')}</Typography>
      </div>
      <div className={classes.content}>
        <TableContainer className={classes.table}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: '220px' }}>{t('campaign')}</TableCell>
                <TableCell sx={{ minWidth: '180px' }}>{t('campaignLead')}</TableCell>
                {hasConnectionRequests ? (
                  <>
                    <TableCell align={'right'} sx={{ minWidth: '150px' }}>
                      {t('sentConnectionRequests')}
                    </TableCell>
                    <TableCell align={'right'} sx={{ minWidth: '150px' }}>
                      {t('acceptedConnectionRequests')}
                    </TableCell>
                    <TableCell align={'right'} sx={{ minWidth: '150px' }}>
                      {t('connectionRequestsRate')}
                    </TableCell>
                    <TableCell align={'right'} sx={{ minWidth: '170px' }}>
                      {t('avgConnectionRequestAcceptanceTime')}
                    </TableCell>
                  </>
                ) : null}
                <TableCell align={'right'} sx={{ minWidth: '150px' }}>
                  {t('contactedContacts')}
                </TableCell>
                <TableCell align={'right'} sx={{ minWidth: '120px' }}>
                  {t('replies')}
                </TableCell>
                <TableCell align={'right'} sx={{ minWidth: '140px' }}>
                  {t('positiveReplies')}
                </TableCell>
                <TableCell align={'right'} sx={{ minWidth: '140px' }}>
                  {t('negativeReplies')}
                </TableCell>
                <TableCell align={'right'} sx={{ minWidth: '130px' }}>
                  {t('responseRate')}
                </TableCell>
                <TableCell align={'right'} sx={{ minWidth: '170px' }}>
                  {t('avgLeadToConversionTime')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!_.isEmpty(performanceComparison) ? (
                performanceComparison.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.funnelName || campaignIdToName(item.funnelId)}</TableCell>
                    <TableCell>{item.linkedinAccountName || '-'}</TableCell>
                    {hasConnectionRequests ? (
                      <>
                        <TableCell align={'right'}>{item.sentConnectionRequestsCount}</TableCell>
                        <TableCell align={'right'}>{item.acceptedConnectionRequestsCount}</TableCell>
                        <TableCell align={'right'}>
                          {item.sentConnectionRequestsCount > 0
                            ? ((item.acceptedConnectionRequestsCount / item.sentConnectionRequestsCount) * 100).toFixed(1)
                            : '0.0'}
                          %
                        </TableCell>
                        <TableCell align={'right'}>
                          {item.avgConnectionRequestAcceptanceTime > 0 ? formatConversionTime(item.avgConnectionRequestAcceptanceTime) : '-'}
                        </TableCell>
                      </>
                    ) : null}
                    <TableCell align={'right'}>{item.contactedContactsCount}</TableCell>
                    <TableCell align={'right'}>{item.messageReplyCount}</TableCell>
                    <TableCell align={'right'}>{item.positiveReplyCount}</TableCell>
                    <TableCell align={'right'}>{item.negativeReplyCount}</TableCell>
                    <TableCell align={'right'}>
                      {item.contactedContactsCount > 0 ? ((item.messageReplyCount / item.contactedContactsCount) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                    <TableCell align={'right'}>{item.avgLeadToConversionTime > 0 ? formatConversionTime(item.avgLeadToConversionTime) : '-'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    {t('noData')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  performanceComparisonTable: {
    background: '#FFF',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
  },
  content: {
    padding: 0,
  },
  table: {
    background: '#FFF',
    '& th': {
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: theme.palette.greys.medium,
      padding: '12px 24px',
      borderBottom: 'none',
      whiteSpace: 'nowrap',
    },
    '& td': {
      fontSize: theme.typography.pxToRem(15),
      padding: '14px 24px',
      fontVariantNumeric: 'tabular-nums',
      borderBottom: 'none',
      borderTop: `1px solid ${theme.palette.greys.lightest}`,
    },
  },
}));

export default PerformanceComparisonTable;
